import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Select, Store } from "@ngxs/store";
import { CompactType, DisplayGrid, GridsterConfig, GridsterItem, GridType } from "angular-gridster2";
import { InitPageState } from "./report-page-state/report-page.state-actions";
import { PageState } from "./report-page-state/report-page.state";
import { Observable, Subject } from "rxjs";
import { PageModel, PageStateModel } from "./report-page-state/report-page.state-models";
import { UpdateReportComponent } from "../report-components/report-component.state-actions";
import { Status } from "@core/data";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { Snackbar } from "@core/material";
import { AdvancedReportState, editModeCheck } from "../report-view/report-state/advanced-report.state";
import { clampComponentGeometry, COMPONENT_OVERLAP_WARNING, PageLongEdge, PageOrientation, PageShortEdge, ReportOptionConfig, resolveGridDensity, resolveGridVisibility, resolveReportMargin, scaleGridOptions } from "@core/data/types/advanced-report";

@Component({
    selector: 'pv-report-page',
    templateUrl: 'report-page.view.html',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    host: {
        class: 'pv-report-page'
    }
})
export class ReportPageView implements OnInit, OnDestroy {

    //? MAIN STATE
    pageState$: Observable<PageModel>;

    @Select(AdvancedReportState.editMode)
    edit$: Observable<boolean>;

    @Input()
    key: string;

    @Input()
    reportKey: string;

    @Input()
    shareKey: string = null;

    @Input()
    index: number;

    @Input()
    total: number;

    @Input()
    editMode: boolean = false;

    options: GridsterConfig;

    //? Margin guide (edit-mode only) as a % inset of the page; 0 = hidden.
    marginXPercent: number = 0;
    marginYPercent: number = 0;

    rowMax: number = 44;
    colMax: number = 31;

    private _destroy$ = new Subject();
    private _componentUpdate$ = new Subject<GridsterItem>();

    // Component keys already warned this page-view instance for a stuck (clamp-was-a-no-op) notPlaced
    // state, so itemInitCallback doesn't re-fire the same warning on every single render.
    private _warnedStuckComponents = new Set<string>();

    constructor(
        private _store: Store,
        private _changes: ChangeDetectorRef,
        private _snackbar: Snackbar,
    ) {}

    ngOnInit() {

        //? DEFAUL GRIDSTER CONFIG
        this.options = {
            //? Fit scales cells to fill the page exactly, avoiding the unplaceable
            //? fractional-cell strip a fixed 25px grid leaves on the right/bottom of A4.
            gridType: GridType.Fit,
            fixedColWidth: 25,
            fixedRowHeight: 25,
            //? Grid min == max so the page always shows the full grid (Fit sizes columns
            //? from minCols/widget-extent, not maxCols); forced again after density scaling.
            minRows: PageLongEdge,
            maxRows: PageLongEdge,
            minItemRows: 2,
            maxItemRows: PageLongEdge,
            defaultItemRows: PageLongEdge,
            minCols: PageShortEdge,
            maxCols: PageShortEdge,
            minItemCols: 2,
            maxItemCols: PageShortEdge,
            defaultItemCols: PageShortEdge,
            displayGrid: DisplayGrid.OnDragAndResize,
            compactType: CompactType.None,
            pushResizeItems: false,
            pushItems: false,
            swap: true,
            //? Zero margins so the Fit grid fills the page edge-to-edge and aligns flush to it
            //? (asymmetric outer margins previously offset the grid from the page's right/bottom).
            margin: 0,
            outerMargin: false,
            outerMarginTop: 0,
            outerMarginRight: 0,
            outerMarginBottom: 0,
            outerMarginLeft: 0,
            //? LAYERING OPTIONS (requires 'layerIndex' on gridster items to function)
            allowMultiLayer: true,
            defaultLayerIndex: 1,
            maxLayerIndex: 4,
            baseLayerIndex: 1,
            draggable: {
                enabled: true,
            },
            resizable: {
                enabled: true,
            },
            disableScrollVertical: true,
            disableScrollHorizontal: true,
            disableWarnings: false,
            itemChangeCallback: (changedItem) => {
                if (!changedItem || !changedItem.hasOwnProperty('itemRef')) return;

                if (!editModeCheck(this.editMode)) return;

                //? TRIGGER COMPONENT UPDATES
                this._componentUpdate$.next(changedItem);
            },
            itemInitCallback: (item, itemComponent) => {
                // TODO move components to the 'loading area' once that logic is in place
                if (!itemComponent.notPlaced) return;

                // Previously this deleted the component outright. A real incident (2026-07-31) showed
                // this can silently destroy user data: after a density/orientation rescale, gridster's
                // own placement algorithm can mark a component "not placed" (e.g. it now overlaps a
                // sibling) even though our own bounds-clamp already ran on save — and every subsequent
                // page render re-triggers this callback, deleting more components each time. Never
                // destroy data just because the grid couldn't cleanly place it — clamp it back into the
                // current page bounds instead (the same clampComponentGeometry helper Epic 1 already
                // uses for exactly this purpose) and save the recovered position, so the component
                // stays visible and editable rather than vanishing.
                const maxCols = this.options.maxCols;
                const maxRows = this.options.maxRows;

                const clamped = clampComponentGeometry(
                    { x: item.x, y: item.y, width: item.cols, height: item.rows },
                    maxCols,
                    maxRows
                );

                const changed = clamped.x !== item.x || clamped.y !== item.y
                    || clamped.width !== item.cols || clamped.height !== item.rows;

                const itemKey: string | undefined = item['itemRef'];

                if (!changed) {
                    // Already within page bounds but still "not placed" — the conflict is with a
                    // sibling component's position (overlap), not the page edge, which a page-bounds
                    // clamp can't resolve on its own. Re-dispatching the same values would just
                    // re-trigger this same callback forever, so leave it in place (visually overlapping
                    // is far better than being silently deleted) and only warn once per component —
                    // itemInitCallback fires on every render, and without this the same warning would
                    // otherwise repeat indefinitely for a component that stays stuck. No itemKey (a
                    // transient/internal gridster item, not one of ours) always warns rather than
                    // being deduped against unrelated items sharing an undefined key.
                    if (!itemKey || !this._warnedStuckComponents.has(itemKey)) {
                        if (itemKey) this._warnedStuckComponents.add(itemKey);
                        this._snackbar.error(COMPONENT_OVERLAP_WARNING);
                    }
                    return;
                }

                // Actively being fixed — clear any prior stuck-warning so a future, different stuck
                // state for this component (unlikely, but not impossible) can warn again.
                if (itemKey) this._warnedStuckComponents.delete(itemKey);

                this._snackbar.error("A component didn't fit the page and was repositioned to stay visible.");

                this._store.dispatch(new UpdateReportComponent(item['itemRef'], {
                    x: clamped.x,
                    y: clamped.y,
                    cols: clamped.width,
                    rows: clamped.height,
                }));
            }
        };

        this._store.dispatch(new InitPageState(this.key, this.reportKey, this.shareKey));

        this.pageState$ = this._store.select(PageState.getPage(this.key));

        //? SUBSCRIBE TO UNIQUE COMPONENT MOVE | RESIZE OPTIONS
        this._componentUpdate$.pipe(
            debounceTime(100),
            distinctUntilChanged(),
            takeUntil(this._destroy$)
        ).subscribe((item: GridsterItem) => {
            if (!editModeCheck(this.editMode)) return;
            this._store.dispatch(new UpdateReportComponent(item['itemRef'], item));
        });

        this.pageState$.pipe(
            takeUntil(this._destroy$)
        ).subscribe((state: PageModel) => {
            if (!state.page) return;
            if (state.status != Status.OK) return;

            let overrides: GridsterConfig =  {
                swap: true,
            }

            if (!this.editMode) {
                overrides = {
                    resizable: {
                        enabled: false
                    },
                    draggable: {
                        enabled: false
                    },
                }
            }

            const reportModel = this._store.selectSnapshot(AdvancedReportState.getReport(this.reportKey));
            const density = resolveGridDensity(reportModel?.report?.options?.config);

            const scaled = scaleGridOptions({
                ...this.options,
                ...state.page.options?.config?.gridOptions,
                ...overrides,
            }, density);

            //? Force the grid to the full page regardless of any stored minCols/minRows,
            //? so Fit renders the whole page grid (not a content-sized one) for every report.
            //? displayGrid set after the page-gridOptions spread so the report-level choice wins.
            //? minItemCols/minItemRows floored to 2 regardless of any stored (older, floor-of-1)
            //? page config, so no component can be resized/placed smaller than 2x2.
            this.options = {
                ...scaled,
                minCols: scaled.maxCols,
                minRows: scaled.maxRows,
                minItemCols: Math.max(scaled.minItemCols, 2),
                minItemRows: Math.max(scaled.minItemRows, 2),
                displayGrid: this.resolveDisplayGrid(reportModel?.report?.options?.config, this.editMode),
            };

            this.updateMarginGuide(reportModel?.report?.options?.config);
        })

        //? React to report-level option changes (e.g. margin edits) so the guide updates live
        //? without a page-state change.
        this._store.select(AdvancedReportState.getReport(this.reportKey)).pipe(
            takeUntil(this._destroy$)
        ).subscribe((reportModel) => {
            this.updateMarginGuide(reportModel?.report?.options?.config);
            this.applyGridVisibility(reportModel?.report?.options?.config, this.editMode);
        })

        this.edit$.pipe(
            takeUntil(this._destroy$)
        ).subscribe((value: boolean) => {
            let overrides: GridsterConfig =  {
                resizable: {
                    enabled: value
                },
                draggable: {
                    enabled: value
                },
            }

            this.options = {
                ...this.options,
                ...overrides,
            }

            const reportModel = this._store.selectSnapshot(AdvancedReportState.getReport(this.reportKey));
            this.applyGridVisibility(reportModel?.report?.options?.config, value);
        })

    }

    //? Grid visibility is an edit-mode affordance — viewers keep the base behaviour (never visible).
    private resolveDisplayGrid(config: ReportOptionConfig, editMode: boolean): DisplayGrid {
        return editMode ? resolveGridVisibility(config) : DisplayGrid.OnDragAndResize;
    }

    //? Reassign options so gridster picks up the change (same pattern as the edit$ overrides).
    private applyGridVisibility(config: ReportOptionConfig, editMode: boolean) {
        const displayGrid = this.resolveDisplayGrid(config, editMode);
        if (!this.options || this.options.displayGrid === displayGrid) return;

        this.options = {
            ...this.options,
            displayGrid,
        };
    }

    //? Margin guide inset as a % of the page; cells/columns cancel out so it stays put
    //? across densities (the margin cell count rescales with the grid).
    private updateMarginGuide(config: ReportOptionConfig) {
        const marginCells = resolveReportMargin(config);
        this.marginXPercent = this.options?.maxCols ? (marginCells / this.options.maxCols) * 100 : 0;
        this.marginYPercent = this.options?.maxRows ? (marginCells / this.options.maxRows) * 100 : 0;
    }

    isPortrait(orientation: PageOrientation): boolean {
        if (orientation != 'portrait') return false;
        return true;
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}